
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface RegistrationSuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email?: string;
}

const RegistrationSuccessDialog = ({
  open,
  onOpenChange,
  email,
}: RegistrationSuccessDialogProps) => {
  const navigate = useNavigate();

  const handleContinue = () => {
    onOpenChange(false);
    navigate('/auth/login');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white/95 backdrop-blur-lg dark:bg-black/90 border border-emerald-500/20">
        <DialogHeader className="flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-4">
            <CheckCircle className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
          </div>
          <DialogTitle className="text-2xl font-semibold text-emerald-700 dark:text-emerald-400">
            Registration Successful!
          </DialogTitle>
          <DialogDescription className="pt-4 text-base text-gray-600 dark:text-gray-300">
            We've sent a verification email to:
            <span className="font-medium mt-2 block text-gray-900 dark:text-white">
              {email ? email : 'your email address'}
            </span>
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-md p-4 mt-3">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-300">
              <p className="font-medium mb-1">Please check your email to verify your account</p>
              <p>If you don't see the email, please check your spam or junk folder.</p>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button 
            onClick={handleContinue} 
            className="w-full bg-gradient-to-r from-botscheme-ocean-blue to-botscheme-emerald-green hover:opacity-90 transition-all"
          >
            Continue to Login
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegistrationSuccessDialog;
